import React, { createContext, useCallback, useContext, useState, ReactNode } from 'react';
import { ProjectWorkerGetDTO } from '@/shared/types/project/projectWorker';

interface ProjectContextType {
  currentProjectName: string;
  setCurrentProjectName: (name: string) => void;
  currentProjectStatus: string;
  setCurrentProjectStatus: (status: string) => void;
  projectRoles: string[];
  setProjectRoles: (roles: string[]) => void;
  projectPermissions: string[];
  setProjectPermissions: (permissions: string[]) => void;
  projectWorker: ProjectWorkerGetDTO | null;
  setProjectWorker: (worker: ProjectWorkerGetDTO | null) => void;
  projectScopeLoading: boolean;
  setProjectScopeLoading: (loading: boolean) => void;
  clearProjectScope: () => void;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export function ProjectProvider({ children }: { children: ReactNode }) {
  const [currentProjectName, setCurrentProjectName] = useState("");
  const [currentProjectStatus, setCurrentProjectStatus] = useState("");
  const [projectRoles, setProjectRoles] = useState<string[]>([]);
  const [projectPermissions, setProjectPermissions] = useState<string[]>([]);
  const [projectWorker, setProjectWorker] = useState<ProjectWorkerGetDTO | null>(null);
  const [projectScopeLoading, setProjectScopeLoading] = useState(false);

  const clearProjectScope = useCallback(() => {
    setProjectRoles([]);
    setProjectPermissions([]);
    setProjectWorker(null);
    setProjectScopeLoading(false);
  }, []);

  return (
    <ProjectContext.Provider value={{
      currentProjectName,
      setCurrentProjectName,
      currentProjectStatus,
      setCurrentProjectStatus,
      projectRoles,
      setProjectRoles,
      projectPermissions,
      setProjectPermissions,
      projectWorker,
      setProjectWorker,
      projectScopeLoading,
      setProjectScopeLoading,
      clearProjectScope,
    }}>
      {children}
    </ProjectContext.Provider>
  );
}

export function useProject() {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
}
